/**
 * The rows of the asks tray (v9.5.3): one component per kind of ask
 * addressed to this screen, each marking itself seen once it is drawn.
 *
 * - `test` (TestPromptRow): the GM asks me for a test; Roll rolls it here
 *   (useAnswerTestAsk), or signals when the call is secret.
 * - `stealth` (QuietRequestRow): a player's quiet move for the GM to roll
 *   or consider, or to dismiss.
 * - `intent` (IntentRow): what a player means to do, for the GM, with Done.
 * - `ram` (RamRow): a player's ram, for the GM to rule the damage (RamRuling).
 * - `area` is `AsksTray`'s own (the tick list dialog).
 *
 * Every row keeps the test id its old surface had, so the specs and the
 * fuzzer's focus families still find it, and carries `data-ask-id`: the
 * fuzzer checks that every ask addressed to a seat has a control on it.
 */
import { useEffect } from 'react';
import common from '../../styles/common.module.css';
import styles from '../../styles/CallForTest.module.css';
import { readTestAsk } from '../../engine/narrative/testAsk';
import { TEST_ASK } from '../../hooks/useTestCalls';
import { QUIET_ASK } from '../../engine/stealth/quietMove';
import { INTENT_ASK, readIntent } from '../../engine/narrative/intents';
import { RAM_ASK, readRamAsk } from '../../engine/actions/ram';
import RamRuling from './RamRuling';

/** Mark an open ask seen once it is on screen. */
function useSeen(ask, asks) {
  const { markSeen } = asks;
  useEffect(() => {
    if (ask.status === 'open') markSeen(ask.id);
  }, [ask.id, ask.status, markSeen]);
}

function TestPromptRow({ ask, asks, answerTest }) {
  useSeen(ask, asks);
  const prompt = readTestAsk(ask.payload);
  if (!prompt) return null;
  return (
    <div className={styles.callLine} data-testid="test-prompt" data-ask-id={ask.id}>
      <span>
        The GM asks {prompt.name} for {prompt.label}
        {prompt.secret ? '. You will not be told the result.' : '.'}
      </span>
      <button type="button" className={common.btnDodge} data-testid="test-prompt-roll" onClick={() => answerTest(ask)}>
        Roll
      </button>
    </div>
  );
}

function QuietRequestRow({ ask, asks, quiet }) {
  useSeen(ask, asks);
  const waiting = quiet?.requests.find((r) => r.id === ask.id);
  if (!waiting) return null;
  return (
    <div className={styles.callLine} data-testid="quiet-request" data-ask-id={ask.id}>
      <span>{waiting.line}</span>
      <button type="button" className={common.btnDodge} data-testid="quiet-roll" onClick={() => quiet.roll(waiting.id)}>
        {waiting.rolls ? 'Roll' : 'Consider'}
      </button>
      <button
        type="button"
        className={common.btnSmall}
        data-testid="quiet-dismiss"
        onClick={() => quiet.dismiss(waiting.id)}
      >
        Dismiss
      </button>
    </div>
  );
}

function IntentRow({ ask, asks }) {
  useSeen(ask, asks);
  const intent = readIntent(ask.payload);
  if (!intent) return null;
  return (
    <div className={styles.callLine} data-testid="intent" data-ask-id={ask.id}>
      <span>
        {intent.name}: {intent.text}
      </span>
      <button
        type="button"
        className={common.btnSmall}
        data-testid="intent-done"
        onClick={() => asks.answer(ask.id, true)}
      >
        Done
      </button>
    </div>
  );
}

function RamRow({ ask, asks }) {
  useSeen(ask, asks);
  const question = readRamAsk(ask.payload);
  if (!question) return null;
  return (
    <RamRuling
      question={question}
      askId={ask.id}
      onSubmit={(ruling) => asks.answer(ask.id, ruling)}
      onCancel={() => asks.cancel(ask.id)}
    />
  );
}

/** The row for each kind of ask the tray draws as a line; `area` is drawn as a dialog by the tray itself. */
export const ROWS = {
  [TEST_ASK]: TestPromptRow,
  [QUIET_ASK]: QuietRequestRow,
  [INTENT_ASK]: IntentRow,
  [RAM_ASK]: RamRow,
};
